import { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import { useTaskManager } from '@/contexts/TaskManagerContext'

/**
 * Adaptive banner that shows active tasks when user navigates away from entity pages.
 *
 * Behavior:
 * - When user is ON the entity page for a task, show nothing (detail page handles it)
 * - When user is AWAY from the entity page, show a compact banner
 * - Batch operations always show a banner (they span multiple entities)
 */
export function ActiveTasksBanner() {
  const location = useLocation()
  const { activeTasks } = useTaskManager()

  // Get all active (processing/pending) tasks
  const activeTasksArray = useMemo(
    () =>
      Array.from(activeTasks.values()).filter(
        (t) => t.status === 'processing' || t.status === 'pending'
      ),
    [activeTasks]
  )

  // Check which tasks should show in the banner
  const tasksToShow = useMemo(() => {
    return activeTasksArray.filter((task) => {
      // Batch tasks always show (they span multiple entities)
      if (task.entityType === 'batch') return true

      // For design/listing tasks, check if user is on that entity's page
      const designPath = `/designs/${task.entityId}`
      const isOnEntityPage =
        location.pathname === designPath || location.pathname.startsWith(`${designPath}/`)

      // Show in banner if NOT on the entity page
      return !isOnEntityPage
    })
  }, [activeTasksArray, location.pathname])

  // Don't render if no tasks to show
  if (tasksToShow.length === 0) return null

  // Get the primary task to display (first one, or batch if exists)
  const primaryTask = tasksToShow.find((t) => t.entityType === 'batch') ?? tasksToShow[0]!
  const additionalCount = tasksToShow.length - 1

  // Build link to entity
  const entityLink =
    primaryTask.entityType === 'batch'
      ? `/batches/${primaryTask.entityId}`
      : `/designs/${primaryTask.entityId}`

  return (
    <div className="border-b border-[hsl(var(--info))]/30 bg-[hsl(var(--info))]/10">
      <div className="mx-auto flex h-10 w-full max-w-6xl items-center justify-between gap-4 px-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-[hsl(var(--info))]" />
          <Link to={entityLink} className="truncate text-sm text-foreground hover:underline">
            {primaryTask.progressMessage}
            {primaryTask.displayName && (
              <span className="ml-1 text-[hsl(var(--info))]">
                ({primaryTask.displayName})
              </span>
            )}
          </Link>
          {additionalCount > 0 && (
            <span className="flex-shrink-0 text-xs text-[hsl(var(--info))]">
              +{additionalCount} more
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Progress value={primaryTask.progress} className="h-1.5 w-24" />
            <span className="w-8 text-right text-xs tabular-nums text-[hsl(var(--info))]">
              {primaryTask.progress}%
            </span>
          </div>
          <Link
            to={entityLink}
            className="text-xs font-medium text-[hsl(var(--info))] hover:text-foreground hover:underline"
          >
            View
          </Link>
        </div>
      </div>
    </div>
  )
}
